/**
 * BoardLinkCustomerModal — quick-action modal for the Portfolio board's
 * Customer column. Wraps the existing CustomerForm in globalSearchMode
 * so the operator searches and picks (or creates) a customer in one
 * step, then writes hosp_properties.customer_id directly. No need to
 * open the property detail modal.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import CustomerForm from '../../../../../components/CustomerForm';
import { useToast } from '../../../../../components/Toast';

interface Props {
  supabase: SupabaseClient;
  propertyId: string;
  propertyName: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

export default function BoardLinkCustomerModal({ supabase, propertyId, propertyName, onClose, onSaved }: Props) {
  const { showToast } = useToast();

  async function onCustomerPicked(customer: Record<string, unknown>) {
    const c = customer as Record<string, string | null>;
    const customerId = (c.id as string) || '';
    if (!customerId) {
      showToast('No customer id returned.', 'error');
      return;
    }
    const { error } = await supabase
      .from('hosp_properties')
      .update({ customer_id: customerId })
      .eq('id', propertyId);
    if (error) {
      showToast(`Could not link customer: ${error.message}`, 'error');
      return;
    }
    showToast(`Customer linked to ${propertyName}.`, 'success');
    await onSaved();
    onClose();
  }

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1100 }}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Link customer to {propertyName}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <CustomerForm
            supabase={supabase}
            onSave={onCustomerPicked}
            onCancel={onClose}
            globalSearchMode
          />
        </div>
      </div>
    </div>
  );
}
